import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from 'app/core/app-config/app-config.interface';
import { AppConfigInterface } from 'app/core/app-config/models';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { Observable, of, throwError } from 'rxjs';

const source: string = 'AppConfigService';

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  // TODO: make it so this can be retrieved from backend
  constructor(@Inject(APP_CONFIG) private readonly _appConfig: AppConfigInterface) { }

  /**
   * getSetting
   *
   * Retrieve a setting from the app config
   *
   * @param key Key in app config for specified setting
   *
   * @returns An observable containing a readonly version of the setting
  */
  public getSetting<K extends keyof AppConfigInterface>(key: K): Observable<Readonly<AppConfigInterface[K]>> {
    try {
      return of(this._appConfig[key]);
    } catch (err) {
      throwError(new Error({
        source,
        message: ErrorMessage.unexpected,
        exception: err,
      }));
    }
  }

  /**
   * getSettings
   *
   * Retrieve one or more settings from the app config
   *
   * @param key Keys in app config for specified settings
   *
   * @returns An observable containing a readonly subset of the settings
  */
  public getSettings<K extends keyof AppConfigInterface>(...keys: K[]): Observable<Readonly<Partial<AppConfigInterface>>> {
    try {
      const settings: Partial<AppConfigInterface> = Object
        .keys(this._appConfig)
        .filter((key: K) => keys.includes(key))
        .reduce(
          (_settings: Partial<AppConfigInterface>, key: K) => {
            _settings[key] = this._appConfig[key];

            return _settings;
          },
          {});

      return of(settings);
    } catch (err) {
      throwError(new Error({
        source,
        message: ErrorMessage.unexpected,
        exception: err,
      }));
    }
  }
}
